import React, { useEffect, useState } from "react";
import Session from "supertokens-auth-react/recipe/session";
import { AlbumSharePreview } from "../types/album";
import { useAlbumShare, useUser } from "../hooks";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import { extractShareInvite, shareInviteErrorMessage } from "../utils/shareInvite";

interface JoinAlbumPanelProps {
  isOpen: boolean;
  disabled?: boolean;
  onJoined: (album: { ownerId: string; albumId: string }) => void;
}

const JoinAlbumPanel: React.FC<JoinAlbumPanelProps> = ({ isOpen, disabled = false, onJoined }) => {
  const { user } = useUser();
  const { previewShare, createShare, isCreating, isPreviewing } = useAlbumShare(user?.id);
  const [pasteValue, setPasteValue] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AlbumSharePreview | null>(null);
  const [resolvedToken, setResolvedToken] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPasteValue("");
      setParseError(null);
      setPreviewError(null);
      setPreview(null);
      setResolvedToken(null);
    }
  }, [isOpen]);

  const lookupInvite = async (raw: string) => {
    const parsed = extractShareInvite(raw);
    if (!parsed.ok) {
      setPreview(null);
      setResolvedToken(null);
      setPreviewError(null);
      setParseError(parsed.reason === "empty" ? null : shareInviteErrorMessage(parsed.reason));
      return;
    }

    setParseError(null);
    setPreviewError(null);
    setResolvedToken(parsed.token);

    try {
      const result = await previewShare(parsed.token);
      setPreview(result);
    } catch (error) {
      setPreview(null);
      setPreviewError(getErrorMessage(handleApiError(error)));
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setPasteValue(text);
      await lookupInvite(text);
    } catch {
      setParseError("Couldn't read the clipboard. Paste the link into the field instead.");
    }
  };

  const handleJoin = async () => {
    if (!resolvedToken || !preview) {
      return;
    }

    if (preview.alreadyJoined) {
      onJoined({ ownerId: preview.ownerId, albumId: preview.albumId });
      return;
    }

    try {
      const share = await createShare(resolvedToken);
      await Session.attemptRefreshingSession();
      onJoined({
        ownerId: share.ownerId,
        albumId: share.albumId,
      });
    } catch (error) {
      setPreviewError(getErrorMessage(handleApiError(error)));
    }
  };

  const isBusy = disabled || isCreating || isPreviewing;
  const mediaLabel = preview
    ? `${preview.mediaCount} ${preview.mediaCount === 1 ? "item" : "items"}`
    : "";
  const permissionLabel = preview?.permission === "upload" ? "Can upload" : "View only";
  const statusNote = preview?.isOwner
    ? "This is your album."
    : preview?.alreadyJoined
      ? "You've already joined this album."
      : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-white/70 leading-relaxed">
        Got a link in Messenger or Messages? Copy it and paste it here — don't open it in that chat's
        browser.
      </p>

      <div className="space-y-2">
        <label htmlFor="invite-link" className="block text-sm font-semibold text-white">
          Invite link
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <textarea
            id="invite-link"
            name="invite-link"
            value={pasteValue}
            onChange={(event) => {
              setPasteValue(event.target.value);
              if (parseError || preview) {
                setParseError(null);
                setPreview(null);
                setResolvedToken(null);
                setPreviewError(null);
              }
            }}
            onBlur={() => {
              if (pasteValue.trim()) {
                void lookupInvite(pasteValue);
              }
            }}
            disabled={isBusy}
            rows={3}
            className="w-full px-4 py-3 text-sm text-white bg-white/10 border-2 border-white/30 rounded-2xl resize-none backdrop-blur-sm focus:outline-none focus:ring-4 focus:border-[rgba(var(--primary-blue-rgb),0.55)] focus:ring-[rgba(var(--primary-blue-rgb),0.18)] disabled:bg-white/5 disabled:cursor-not-allowed placeholder-gray-300"
            placeholder="https://joint.photos/owners/…/albums/…/link?token=…"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={isBusy}
            className="jp-btn jp-btn--ghost jp-btn--sm"
          >
            Paste
          </button>
          <button
            type="button"
            onClick={() => void lookupInvite(pasteValue)}
            disabled={isBusy || !pasteValue.trim()}
            className="jp-btn jp-btn--secondary jp-btn--sm"
          >
            {isPreviewing ? "Looking up…" : "Look up album"}
          </button>
        </div>
      </div>

      {(parseError || previewError) && (
        <div className="flex items-start gap-2 text-red-300">
          <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <span className="text-sm font-medium">{parseError || previewError}</span>
        </div>
      )}

      {preview && (
        <div className="rounded-2xl border border-white/20 bg-white/10 p-4 space-y-2">
          <h3 className="text-lg font-bold text-white">{preview.name}</h3>
          {preview.description ? <p className="text-sm text-white/70">{preview.description}</p> : null}
          <p className="text-sm text-white/80">
            {mediaLabel}
            <span className="text-white/40"> · </span>
            {permissionLabel}
          </p>
          {statusNote ? <p className="text-sm text-white/60">{statusNote}</p> : null}
        </div>
      )}

      <div className="flex gap-4 pt-2">
        <button
          type="button"
          onClick={() => void handleJoin()}
          disabled={isBusy || !preview}
          className="jp-btn jp-btn--primary flex-1"
        >
          {isCreating ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Joining…</span>
            </>
          ) : preview?.alreadyJoined ? (
            <span>Open album</span>
          ) : (
            <span>Join album</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default JoinAlbumPanel;
